import { Link, useNavigate, useSearch } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { ErrorState } from "@/components/states"
import { Skeleton } from "@/components/ui/skeleton"
import type { Artifact } from "@/lib/defs"
import { useFormatNumber, useT } from "@/lib/i18n"
import { useObject, useObjects, useProvenance } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { asArray, asString, isRecord } from "@/lib/utils"
import { useOntology } from "@/workspace/hooks"
import type { Ontology } from "@/workspace/ontology"
import { RefusalNote } from "@/workspace/values"
import { ConnectorHealthPanel } from "./connector-health"
import { dataAreas } from "./areas"
import { DataFrame } from "./frame"
import { ReceiptList, receiptsOf } from "./receipts"
import { SyncHealth } from "@/workbook/sync-health"

/** Object types an artifact's canonical JSON names, by type name or entity artifact id (a mapping's targets, a schema's subjects). */
function typesNamed(artifact: Artifact, ontology: Ontology): string[] {
  const text = JSON.stringify(artifact.payload)
  return [...ontology.types.values()].filter((t) => text.includes(JSON.stringify(t.name)) || text.includes(JSON.stringify(t.artifactId))).map((t) => t.name)
}

function sourceName(artifact: Artifact): string {
  const p = artifact.payload
  const source = p.source
  if (typeof source === "string") return source
  if (isRecord(source)) return asString(source.system) ?? asString(source.name) ?? artifact.name ?? artifact.artifactId
  return artifact.name ?? artifact.artifactId
}

export function SourcesPage() {
  const t = useT()
  const scope = useScope()
  const formatNumber = useFormatNumber()
  const navigate = useNavigate()
  const search = useSearch({ from: "/$orgId/$environmentId/data/sources" })
  const { ontology, defs } = useOntology(scope)
  const artifacts = defs.data?.artifacts ?? []
  const schemas = artifacts.filter((a) => a.kind === "source_schema")
  const mappings = artifacts.filter((a) => a.kind === "mapping")
  const connectors = artifacts.filter((a) => a.kind === "connector")
  const mapped = useMemo(() => (ontology ? [...new Set(mappings.flatMap((m) => typesNamed(m, ontology)))] : []), [mappings, ontology])
  const lookupTypes = useMemo(() => {
    if (!ontology) return []
    const business = dataAreas(ontology).flatMap((a) => a.types)
    return [...new Set([...mapped, ...business])]
  }, [ontology, mapped])
  const type = search.type ?? mapped[0] ?? lookupTypes[0]
  const [draftKey, setDraftKey] = useState("")

  return (
    <DataFrame title={t("dv.nav.sources")} description={t("dv.sources.description")} how={["dv.how.sources"]} crumbs={[t("dv.nav.sources")]}>
      <ConnectorHealthPanel scope={scope} />
      {defs.isError ? (
        <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
      ) : !ontology ? (
        <Skeleton className="h-96 w-full rounded-2xl" />
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="text-[15px] font-semibold tracking-tight">{t("dv.sources.systems")}</h2>
            {schemas.length + connectors.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("dv.sources.noSystems")}</p>
            ) : (
              <ul className="divide-y divide-border rounded-2xl border border-border text-sm">
                {[...schemas, ...connectors].map((a) => (
                  <li key={a.artifactId} className="grid gap-1 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                    <div className="min-w-0">
                      <p className="font-medium">{sourceName(a)}</p>
                      <Link to="/$orgId/$environmentId/definitions/$kind/$artifactId" params={{ ...scope, kind: a.kind, artifactId: a.artifactId }} dir="ltr" className="font-mono text-2xs text-muted-foreground hover:underline">
                        {a.artifactId}@{a.version}
                      </Link>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {a.kind === "connector"
                        ? t("dv.sources.connector")
                        : t("dv.sources.schemaShape", { datasets: formatNumber(asArray(a.payload.datasets).length), fields: formatNumber(asArray(a.payload.fields).length) })}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="text-[15px] font-semibold tracking-tight">{t("dv.sources.mappings")}</h2>
            {mappings.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("dv.sources.noMappings")}</p>
            ) : (
              <ul className="divide-y divide-border rounded-2xl border border-border text-sm">
                {mappings.map((m) => (
                  <li key={m.artifactId} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
                    <Link to="/$orgId/$environmentId/definitions/$kind/$artifactId" params={{ ...scope, kind: "mapping", artifactId: m.artifactId }} dir="ltr" className="font-mono text-xs hover:underline">
                      {m.artifactId}@{m.version}
                    </Link>
                    <span className="text-xs text-subtle-foreground">→</span>
                    {typesNamed(m, ontology).map((name) => (
                      <Link key={name} to="/$orgId/$environmentId/data/ontology" params={scope} search={{ type: name }} className="text-xs hover:underline">
                        {ontology.types.get(name)?.label ?? name}
                      </Link>
                    ))}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <SyncHealth scope={scope} ontology={ontology} installedMappings={new Set(mappings.map((m) => `${m.artifactId}@${m.version}`))} />

          <section className="space-y-4">
            <h2 className="text-[15px] font-semibold tracking-tight">{t("dv.sources.lookup")}</h2>
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                if (draftKey.trim()) void navigate({ to: "/$orgId/$environmentId/data/sources", params: scope, search: { ...(type ? { type } : {}), key: draftKey.trim() } })
              }}
            >
              <select
                aria-label={t("graph.searchType")}
                value={type}
                onChange={(e) => void navigate({ to: "/$orgId/$environmentId/data/sources", params: scope, search: { type: e.target.value } })}
                className="h-9 max-w-xs rounded-full border border-input bg-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
              >
                {lookupTypes.map((name) => (
                  <option key={name} value={name}>
                    {ontology.types.get(name)?.pluralLabel ?? name}
                    {mapped.includes(name) ? ` · ${t("dv.sources.mappedTag")}` : ""}
                  </option>
                ))}
              </select>
              <input
                value={draftKey}
                onChange={(e) => setDraftKey(e.target.value)}
                placeholder={search.key ?? t("dv.sources.keyPlaceholder")}
                aria-label={t("dv.sources.keyPlaceholder")}
                className="h-9 w-56 rounded-full border border-input bg-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
              />
              <button type="submit" className="h-9 rounded-full border border-border px-4 text-sm hover:bg-muted/60">
                {t("dv.sources.show")}
              </button>
            </form>
            {type ? <Lookup key={`${type}/${search.key ?? ""}`} type={type} chosenKey={search.key} ontology={ontology} /> : null}
          </section>
        </>
      )}
    </DataFrame>
  )
}

/** The record to show: the chosen key, else the first readable record of the type (keyed per type, no placeholder carry-over). */
function Lookup({ type, chosenKey, ontology }: { type: string; chosenKey?: string; ontology: Ontology }) {
  const t = useT()
  const scope = useScope()
  const sample = useObjects(scope, type, null, 1, !chosenKey)
  if (chosenKey) return <RecordSources type={type} objectKey={chosenKey} ontology={ontology} />
  if (sample.isPending) return <Skeleton className="h-24 w-full rounded-2xl" />
  if (sample.isError) return <RefusalNote error={sample.error} title={t("dv.explore.refused")} />
  const first = sample.data.items[0]
  return first ? <RecordSources type={type} objectKey={first.key} ontology={ontology} /> : <p className="text-sm text-muted-foreground">{t("dv.explore.empty")}</p>
}

/** Where one record's values came from: source receipts when it was observed, else its writing action. */
function RecordSources({ type, objectKey, ontology }: { type: string; objectKey: string; ontology: Ontology }) {
  const t = useT()
  const scope = useScope()
  const object = useObject(scope, type, objectKey)
  const provenance = useProvenance(scope, type, objectKey, object.isSuccess)
  const meta = ontology.types.get(object.data?.objectType ?? type)
  const receipts = receiptsOf(provenance.data, object.data?.version)
  if (object.isPending || (object.isSuccess && provenance.isPending)) return <Skeleton className="h-24 w-full rounded-2xl" />
  if (object.isError) return <RefusalNote error={object.error} title={t("ws.record.notVisible")} />
  if (provenance.isError) return <RefusalNote error={provenance.error} title={t("ws.why.evidenceUnavailable")} />
  return (
    <div className="space-y-3">
      <p className="text-sm">
        <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: object.data.objectType, key: objectKey }} className="font-medium hover:underline">
          {meta?.label ?? type} {objectKey}
        </Link>
        <span className="text-muted-foreground"> · v{object.data.version}</span>
      </p>
      {receipts.length ? (
        <ReceiptList receipts={receipts} labelOf={(id) => meta?.fields.find((f) => f.id === id)?.label ?? id} />
      ) : (
        <p className="rounded-xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          {t("dv.sources.noReceipts")}
          {provenance.data?.historyTruncated ? ` ${t("ws.why.truncated")}.` : ""}
          {object.data.masked && Object.keys(object.data.masked).length ? ` ${t("dv.sources.someMasked")}` : ""}{" "}
          <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: object.data.objectType, key: objectKey }} className="underline underline-offset-4 hover:text-foreground">
            {t("dv.sources.openWhy")}
          </Link>
        </p>
      )}
    </div>
  )
}
